import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { PageHeaderComponent } from '../core/labels/page-header.component';
import { HomeCardComponent } from '../home/home-card.component';
import { PanelComponent } from '../core/panels/panel.component';
import { InputComponent } from '../core/controls/input.component';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { BaseComponent } from '../base/base.component';
import { RequiredFieldsNoteComponent } from '../core/labels/required-fields-note.component';
import { PasswordRequirementsComponent } from '../core/controls/password-requirements.component';
import { ToastComponent } from '../core/labels/toast.component';

@Component({
  selector: 'app-password-change',
  imports: [
    RouterModule,
    PageHeaderComponent,
    HomeCardComponent,
    PanelComponent,
    InputComponent,
    ReactiveFormsModule,
    ActionRowComponent,
    ButtonComponent,
    RequiredFieldsNoteComponent,
    PasswordRequirementsComponent,
    ToastComponent,
  ],
  template: `
    <app-page-header label="Preferences" headerSize="h1" />
    <div class="tw-grow tw-w-full tw-justify-center">
      <app-home-card type="Large">
        <div class="tw-p-0 md:tw-p-8 tw-w-full tw-min-w-0">
          <app-panel>
            <app-page-header label="Change Password" headerSize="h4" />
            <div
              class="tw-flex tw-justify-center"
              [class.tw-hidden]="!errorNewPasswordMismatch"
              [attr.aria-hidden]="!errorNewPasswordMismatch ? 'true' : null"
            >
              <app-toast
                type="Error"
                [dismissSeconds]="10"
                [dismiss]="true"
                (dismissed)="errorNewPasswordMismatch = false"
              >
                Error: The new password entries do not match. Please
                re-enter.</app-toast
              >
            </div>
            <form [formGroup]="fg" (ngSubmit)="onSubmit()">
              <div
                class="tw-flex tw-w-full tw-items-center tw-flex-col tw-justify-center"
              >
                <div
                  class="tw-flex tw-flex-col tw-p-0 md:tw-p-4 tw-min-w-0 tw-max-w-[400px]"
                >
                  <app-required-fields-note class="tw-mb-4" />
                </div>
                <div
                  class="tw-flex tw-flex-col tw-p-0 md:tw-p-2 tw-min-w-0 tw-max-w-[250px]"
                >
                  <div class="tw-col-span-3 tw-flex">
                    <app-input
                      type="Password"
                      [control]="fg.controls.oldPassword"
                      label="Old password"
                      [large]="true"
                      [showReveal]="true"
                      autocomplete="new-password"
                    />
                  </div>
                </div>
                <div
                  class="tw-flex tw-flex-col tw-p-0 md:tw-p-2 tw-min-w-0 tw-max-w-[250px]"
                >
                  <div class="tw-col-span-3 tw-flex">
                    <app-input
                      type="Password"
                      [control]="fg.controls.newPassword"
                      label="New password"
                      [large]="true"
                      [showReveal]="true"
                      autocomplete="new-password"
                    />
                  </div>
                </div>
                <div
                  class="tw-flex tw-flex-col tw-p-0 md:tw-p-2 tw-min-w-0 tw-max-w-[250px]"
                >
                  <div class="tw-col-span-3 tw-flex">
                    <app-input
                      type="Password"
                      [control]="fg.controls.confirmNewPassword"
                      label="Retype new password"
                      [large]="true"
                      [showReveal]="true"
                      autocomplete="new-password"
                    />
                  </div>
                </div>
                <div
                  class="tw-flex tw-flex-col tw-p-0 md:tw-p-4 tw-pt-4 tw-min-w-0 tw-max-w-[400px]"
                >
                  <app-password-requirements [error]="passwordError" />
                </div>
              </div>
              <div class="tw-flex tw-gap-2 tw-pt-6">
                <app-action-row class="tw-w-full">
                  <app-button
                    right1
                    type="submit"
                    label="Submit"
                    emphasis="High"
                    [loading]="loading"
                    [disabled]="!fg.valid"
                  />
                  <app-button
                    right2
                    label="Cancel"
                    routerLink="/preferences"
                    emphasis="Low"
                  />
                </app-action-row>
              </div>
            </form>
          </app-panel>
        </div>
      </app-home-card>
    </div>
  `,
  styles: ``,
})
export class PasswordChangeComponent extends BaseComponent implements OnInit {
  errorNewPasswordMismatch = false;
  passwordError = false;
  loading = false;

  fg = new FormGroup({
    oldPassword: new FormControl<string>(null, [Validators.required]),
    newPassword: new FormControl<string>(null, [
      Validators.required,
      Validators.minLength(8),
    ]),
    confirmNewPassword: new FormControl<string>(null, [Validators.required]),
  });

  ngOnInit(): void {
    if (this.session.user?.isSsoUser) {
      this.router.navigate(['/preferences']);
      return;
    }
  }

  async onSubmit(): Promise<void> {
    this.loading = true;
    this.passwordError = false;

    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      this.loading = false;
      return;
    }

    if (
      this.fg.controls.newPassword.value !==
      this.fg.controls.confirmNewPassword.value
    ) {
      this.errorNewPasswordMismatch = true;
      this.loading = false;
      return;
    }

    try {
      const response = await this.api.auth.changePassword({
        oldPassword: this.fg.controls.oldPassword.value,
        newPassword: this.fg.controls.newPassword.value,
      });

      if (response.result === 'Success') {
        this.fg.reset();
        this.router.navigate(['/preferences'], {
          queryParams: { passwordChanged: true },
        });
      } else {
        this.passwordError = true;
      }
    } catch (e) {
      this.passwordError = true;
    } finally {
      this.loading = false;
    }
  }
}
